import { Spinner } from '@consigli/facade';
import {
  useDownloadFilesAsZip,
  useLazyGetBlobsQuery,
  usePackage,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { convertBlobToDocument } from '@consigli/types';
import { useCallback } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';

import { errorToast, infoToast } from '@/components/toast';

/**
 * Button that downloads every document uploaded to the package as a single ZIP
 * file, named after the package.
 */
export const DownloadAllDocumentsButton = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { pkg } = usePackage(projectId, packageId);
  const [getBlobs, { isLoading: isFetchingBlobs }] = useLazyGetBlobsQuery();
  const { downloadFileZip, isDownloading } = useDownloadFilesAsZip(
    pkg?.name || 'document-package',
    false,
  );

  const handleDownload = useCallback(async () => {
    try {
      const blobs = await getBlobs({ projectId, packageId, page: 'all' }).unwrap();

      if (blobs.results.length === 0) {
        infoToast({ message: 'This package has no documents to download' });
        return;
      }

      await downloadFileZip(blobs.results.map(convertBlobToDocument));
    } catch (error) {
      console.error('Failed to download package documents as ZIP:', error);
      errorToast({ message: 'Failed to download documents' });
    }
  }, [getBlobs, projectId, packageId, downloadFileZip]);

  const isBusy = isFetchingBlobs || isDownloading;

  return (
    <Button variant="outline" onClick={handleDownload} disabled={isBusy}>
      {isBusy ? <Spinner size="xsmall" /> : <MdOutlineFileDownload className="size-5" />}
      {isBusy ? 'Downloading...' : 'Download all'}
    </Button>
  );
};
